<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/chemicals/templates">Templates</a></li>
        <li class="breadcrumb-item active">View Template</li>
      </ol>
    </nav>
    <div class="d-flex align-items-center gap-3">
      <h1 class="h2 mb-0 fw-bold">{{ template?.name || 'Chemical Template' }}</h1>
      <span *ngIf="template" class="badge bg-info-subtle text-info">
        <i class="bi bi-arrow-repeat me-1"></i>Used {{ template.usageCount }} times
      </span>
    </div>
    <p *ngIf="template?.description" class="text-muted mt-2 mb-0">{{ template?.description }}</p>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-secondary" (click)="duplicateTemplate()" title="Duplicate functionality coming soon" disabled>
      <i class="bi bi-copy me-2"></i>Duplicate
    </button>
    <button class="btn btn-outline-danger" (click)="confirmDelete()">
      <i class="bi bi-trash me-2"></i>Delete
    </button>
    <button class="btn btn-outline-primary" (click)="editTemplate()">
      <i class="bi bi-pencil me-2"></i>Edit
    </button>
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="bi bi-arrow-left me-2"></i>Back
    </button>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading template details...</p>
</div>

<!-- Error State -->
<div *ngIf="!isLoading && errorMessage" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
</div>

<!-- Main Content -->
<div *ngIf="!isLoading && template" class="row">
  <div class="col-lg-8">

    <!-- Section 1: Chemicals (Primary Content) -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-flask me-2"></i>Chemicals ({{ template.items?.length || 0 }})</h5>
      </div>
      <div class="card-body p-0">
        <ng-container *ngIf="template.items && template.items.length > 0; else noChemicals">
          <div class="table-responsive">
            <table class="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th class="ps-3">Chemical Name</th>
                  <th>Purpose</th>
                  <th>Application Rate</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of template.items; trackBy: trackByIndex">
                  <td class="ps-3 fw-medium">{{ item.name }}</td>
                  <td>{{ item.purpose || '-' }}</td>
                  <td class="text-primary fw-medium">{{ item.rate }}</td>
                  <td>
                    <span class="badge" [ngClass]="getSourceBadgeClass(item.source)">
                      {{ getSourceLabel(item.source) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <ng-template #noChemicals>
          <p class="text-muted mb-0 text-center py-4">
            <i class="bi bi-info-circle me-2"></i>No chemicals in template
          </p>
        </ng-template>
      </div>
    </div>

    <!-- Section 2: Application Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Application Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Total Spray Volume</label>
            <p class="mb-0 fw-medium">{{ template.totalVolume }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Application Timing</label>
            <p class="mb-0 fw-medium">{{ template.applicationTiming || 'Not specified' }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Estimated Cost</label>
            <p class="mb-0 fw-medium">{{ formatCurrency(template.estimatedCostPerAcre) }}<span *ngIf="template.estimatedCostPerAcre">/acre</span></p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Target Crops -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-flower1 me-2"></i>Target Crops ({{ template.targetCrops ? template.targetCrops.length : 0 }})</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="template.targetCrops && template.targetCrops.length > 0; else noCrops">
          <div class="d-flex flex-wrap gap-2">
            <span *ngFor="let crop of template.targetCrops; trackBy: trackByIndex"
                  class="badge rounded-pill bg-success-subtle text-success">
              {{ crop }}
            </span>
          </div>
        </ng-container>
        <ng-template #noCrops>
          <p class="text-muted mb-0 text-center py-3">
            <i class="bi bi-info-circle me-2"></i>No target crops specified
          </p>
        </ng-template>
      </div>
    </div>

    <!-- Section 4: Target Pests (only show if present) -->
    <div class="card mb-4" *ngIf="template.targetPests && template.targetPests.length > 0">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-bug me-2"></i>Target Pests ({{ template.targetPests.length }})</h5>
      </div>
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2">
          <span *ngFor="let pest of template.targetPests; trackBy: trackByIndex"
                class="badge rounded-pill bg-danger-subtle text-danger">
            {{ pest }}
          </span>
        </div>
      </div>
    </div>

    <!-- Section 5: Additional Notes -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="template.notes; else noNotes">
          <div class="p-3 bg-body-tertiary rounded border">
            <p class="mb-0 text-body">{{ template.notes }}</p>
          </div>
        </ng-container>
        <ng-template #noNotes>
          <p class="mb-0 text-muted fst-italic">No additional notes</p>
        </ng-template>
      </div>
    </div>

  </div>

  <!-- Right Column - Metadata Sidebar -->
  <div class="col-lg-4">
    <div class="sticky-top sticky-sidebar">
    <div class="card">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-clipboard-data me-2"></i>Template Info</h5>
      </div>
      <div class="card-body">
        <!-- Quick Stats -->
        <div class="row g-2 mb-3">
          <div class="col-4 text-center">
            <div class="p-2 bg-body-tertiary rounded">
              <div class="fs-4 fw-bold text-primary">{{ template.items?.length || 0 }}</div>
              <div class="small text-muted">Chemicals</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="p-2 bg-body-tertiary rounded">
              <div class="fs-4 fw-bold text-success">{{ template.targetCrops ? template.targetCrops.length : 0 }}</div>
              <div class="small text-muted">Crops</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="p-2 bg-body-tertiary rounded">
              <div class="fs-4 fw-bold text-danger">{{ template.targetPests ? template.targetPests.length : 0 }}</div>
              <div class="small text-muted">Pests</div>
            </div>
          </div>
        </div>

        <hr class="my-3">

        <!-- Usage & Dates -->
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Usage Count</label>
          <p class="mb-0">
            <span class="badge bg-info-subtle text-info">
              <i class="bi bi-arrow-repeat me-1"></i>Used {{ template.usageCount }} times
            </span>
          </p>
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Created</label>
          <p class="mb-0">{{ template.createdAt | adnDate }}</p>
        </div>

        <hr class="my-3">

        <!-- Action Buttons -->
        <div class="d-grid gap-2">
          <button type="button" class="btn btn-primary" (click)="useTemplate()">
            <i class="bi bi-play-circle me-2"></i>Use Template
          </button>
          <button type="button" class="btn btn-outline-primary" (click)="editTemplate()">
            <i class="bi bi-pencil me-2"></i>Edit Template
          </button>
          <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
            <i class="bi bi-arrow-left me-2"></i>Back to List
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()">
</app-confirm-modal>
